import 'idempotent-babel-polyfill'
import { lumber } from '@mc-alberta/lumber'
import winterfell from '@mc-alberta/winterfell'
import { SDKAggregator } from '@mc-alberta/sdk-aggregator'
import * as i18nData from '@mc-alberta/i18n-data'
import state from './state'
import initializer from './helpers/initializer'
import { SDKS } from './constants'
import { devLogger, prodLogger } from './utils/aggregatorLogger'
import getLocalStorageItem from './utils/getLocalStorageItem'
import events from '../events.json'

/**
 * Initializes up merchant js and its dependencies.
 */
export default function bootstrap () {
  state.isProduction = new URL(document.currentScript.src).origin === 'https://src.mastercard.com'
  state.isLocalhost = !!new URL(document.currentScript.src).origin.match(/localhost/g)
  // QS Params of a script can only be read during its initial execution (not during `checkout()`)
  readQueryStringParams()

  // Wait for DOM to be loaded before attempting to initialize SRC asynchronously
  if (document.readyState && document.readyState !== 'loading') {
    startInitialization()
  } else {
    window.addEventListener('DOMContentLoaded', startInitialization, false)
  }
}

/**
 * Reads the expected query string parameters in the Merchant.js URL
 * - `checkoutid` required
 * - `locale` optional
 */
function readQueryStringParams () {
  const urlParams = new URLSearchParams(document.currentScript.src.split('?').pop())
  // Note: document.currentScript is only ever accessible during its initialization (not when calling `checkout()`).
  // This is intentional so its reference can be garbage collected.
  state.checkoutId = urlParams.get('checkoutid')
  if (!state.checkoutId) throw new Error('Failed to read checkoutId in query string params from merchant script')
  // Read locale
  state.locale = i18nData.formatLocale(urlParams.get('locale'), '_') || 'en_US'
}

/**
 * Sets up the mastercard communications, the remote logging, and the SDKs via the SDK manager.
 * Then sets up a default transaction and gets the available profiles all before the button press.
 */
async function startInitialization () {
  try {
    await initializeSrcLib(state.checkoutId)
  } catch (error) {
    /**
     * Sets a flag in our SRC initializer when there was an error
     * initializing default transaction. This allows our initializer to
     * immediately bail on the init process and throw the error to our
     * SRCi
     */
    initializer.initializationError = error
  }
}

/**
 * Bootstrap mastercard communications, as well as initialize
 * SDKS and get available profiles all before the button press.
 *
 * @param {*} checkoutId - from the current script src
 */
async function initializeSrcLib (checkoutId) {
  // initialize srci middleware and lumber
  const {
    srciTransactionId,
    dpaTransactionOptions,
    dpaData
  } = await initializeFirstPartyLibs()

  // initialize the sdk aggregator (and underlying sdks)
  await initializeSdkAggregator(winterfell.disabledSdks(), {
    srciTransactionId,
    dpaTransactionOptions,
    dpaData,
    srcDpaId: checkoutId
  })

  initializer.getAvailableProfiles()
}

/**
 * Initializes Winterfell and Lumber (consuming the Winterfell Iframe for its requests).
 * Performs the `/get-default-settings` calls
 */
async function initializeFirstPartyLibs () {
  // initialize srci middleware manager
  await winterfell.bootstrap({
    useXhr: false,
    traceId: state.traceId,
    locale: state.locale
  })

  // initialize lumber using the middleware iframe
  lumber.bootstrap({
    name: 'MERCHANT.JS',
    version: process.env.APP_VERSION,
    traceId: state.traceId,
    iframeConfig: {
      ref: document.getElementById('MASTERCARD_MIDDLEWARE_IFRAME').contentWindow, // proxy lumber request via existing middleware iframe
      message: 'MIDDLEWARE_IFRAME_REQUEST',
      domain: process.env.MASTERCARD_SRC_HOST
    },
    delay: 1500
  })
  lumber.setLogConstants({ checkoutId: state.checkoutId })

  // call get default settings as we wait for the user to click the button (so we get the merchant runtime parameters)
  const {
    srciTransactionId,
    dpaTransactionOptions,
    dpaData
  } = await winterfell.initializeDefaultTransaction(state.checkoutId)
  lumber.setHeaderConstants({ 'srci-transaction-id': srciTransactionId })

  return {
    srciTransactionId,
    dpaTransactionOptions,
    dpaData
  }
}

/**
 * Initializes the sdk aggregator and its sdks.
 */
async function initializeSdkAggregator (disabledSdks, params) {
  // instantiate aggregator
  state.aggregator = new SDKAggregator({
    logger: state.isProduction ? prodLogger('SRCI') : devLogger('SRCI'),
    config: {
      sdks: {
        loadTimeoutMs: 15000 // overrides SDK load timeout
      },
      srcMethods: {
        timeoutMs: process.env.SDK_TIMEOUT || 6000, // overrides SRC method timeout
        checkout: {
          timeoutMs: 0 // a value of 0 (zero) disables timeout
        }
      },
      enableNonBlockingValidation: true
    }
  })

  // attach to window only on lower env for dev purposes
  if (!state.isProduction) {
    window.aggregator = state.aggregator
  }

  // build SDK configs based on disabledSdks
  const sdkConfigs = Object.keys(SDKS).reduce((config, networkId) => {
    if (!disabledSdks.includes(networkId) && SDKS[networkId].URL) {
      config[networkId] = {
        URL: SDKS[networkId].URL
      }
    }
    return config
  }, {})

  // compute sdk specific init params
  const sdkSpecificInitParams = Object.keys(SDKS).reduce((config, networkId) => {
    if (!disabledSdks.includes(networkId) && SDKS[networkId].INITIATOR_ID) {
      // Add initiator id
      config[networkId] = {
        srcInitiatorId: SDKS[networkId].INITIATOR_ID
      }

      // Add traceId for mastercard
      if (networkId === 'mastercard') {
        config[networkId].__ma_params = {
          traceId: state.traceId
        }
      }

      // Add isProduction and override lower env dpa id for visa
      if (networkId === 'visa') {
        config[networkId] = {
          ...config[networkId],
          // TODO: confirm this should only be added for localhost
          ...(state.isLocalhost || getLocalStorageItem('mcsrc.enablesdkoverrides') ? { isProduction: state.isProduction } : {}),
          // TODO: confirm dpa id should no longer be hardcoded on lower env
          ...(!state.isProduction && getLocalStorageItem('mcsrc.enablesdkoverrides') ? { srcDpaId: 'matestdpa2' } : {})
        }
      }

      // Override lower env dpa id for discover
      if (networkId === 'discover') {
        config[networkId] = {
          ...config[networkId],
          // TODO: confirm dpa id should no longer be hardcoded on lower env
          ...(!state.isProduction && getLocalStorageItem('mcsrc.enablesdkoverrides') ? { srcDpaId: 'ca1faacf4d734247be5fce22d0270421' } : {})
        }
      }
    }

    return config
  }, {})

  const sequenceResult = await state.aggregator.sequence.bootstrapToIsRecognized({
    bootstrap: { sdkConfigs },
    init: {
      ...params,
      sdkSpecificParameters: sdkSpecificInitParams
    }
  })
  if (sequenceResult.init?.response) {
    lumber.error({
      ...events.errors.FAILED_TO_INITIALIZE_SDK,
      details: {
        message: 'Could not initialize some of the available SDKs',
        initFailedForNetworks: Object.entries(sequenceResult.init.response).reduce((initFailedForNetworks, [ networkId, result ]) => {
          if (result.status === 'rejected') {
            initFailedForNetworks.push(networkId)
          }
          return initFailedForNetworks
        }, [])
      }
    })
  }
}
